import { useState, useEffect } from "react";
import adminApi from "../api/adminAxios";

const initials = name => name?.slice(0, 2).toUpperCase() || "??";
const avatarColor = name => {
  const colors = ["#888", "#aaa", "#b0a090", "#c0b090", "#a09080"];
  return colors[(name?.charCodeAt(0) || 0) % colors.length];
};

export default function ManageArchives() {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [selected, setSelected] = useState(null);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    adminApi.get("admin/users/")
      .then(r => setUsers(r.data.filter(u => !u.is_active)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = users.filter(u =>
    !search || [u.username, u.email, String(u.id)]
      .some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );

  const handleRestore = async (user) => {
    setRestoring(user.id);
    try {
      await adminApi.patch(`admin/users/${user.id}/`, { is_active: true });
      setUsers(prev => prev.filter(u => u.id !== user.id));
      if (selected?.id === user.id) setSelected(null);
    } catch {}
    finally { setRestoring(null); }
  };

  return (
    <div style={{ padding: "32px 36px", fontFamily: "Georgia, serif" }}>

      {/* Header */}
      <div style={{ marginBottom: "28px" }}>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: "24px", fontWeight: "700", color: "#1a1a1a" }}>
          Archives
        </h1>
        <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#aaa", marginTop: "4px" }}>
          Deactivated user accounts. All data and order history is preserved here.
        </p>
      </div>

      {/* Stats banner */}
      <div style={{ background: "linear-gradient(135deg, #2d2d2d, #1a1a1a)", borderRadius: "14px", padding: "20px 28px", marginBottom: "24px", display: "flex", alignItems: "center", gap: "20px" }}>
        <span style={{ fontSize: "32px" }}>🗂️</span>
        <div>
          <p style={{ fontFamily: "Georgia, serif", fontSize: "22px", fontWeight: "700", color: "#fff" }}>{users.length}</p>
          <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#aaa" }}>Archived Accounts</p>
        </div>
        <div style={{ marginLeft: "32px", paddingLeft: "32px", borderLeft: "1px solid #444" }}>
          <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#aaa", lineHeight: "1.7" }}>
            These accounts have been deactivated. Users cannot log in but their<br />
            order history, addresses, and data are fully preserved.
          </p>
        </div>
      </div>

      {/* Search */}
      <div style={{ background: "#fff", borderRadius: "14px", padding: "16px 20px", border: "1px solid #f0ebe3", marginBottom: "16px" }}>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#bbb" }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search archived users by name, email or ID..."
            style={{ width: "100%", padding: "9px 12px 9px 34px", borderRadius: "9px", border: "1.5px solid #e8d5b8", background: "#fffaf4", fontFamily: "Georgia, serif", fontSize: "12px", outline: "none", boxSizing: "border-box" }} />
        </div>
      </div>

      {/* Table */}
      <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #f0ebe3", overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr 120px 110px 110px 130px", gap: "8px", padding: "12px 20px", background: "#f7f2eb" }}>
          {["USER ID", "USERNAME", "EMAIL", "CONTACT", "ORDERS", "DEACTIVATED", "ACTIONS"].map(h => (
            <span key={h} style={{ fontFamily: "Georgia, serif", fontSize: "9px", fontWeight: "700", color: "#aaa", letterSpacing: "0.08em" }}>{h}</span>
          ))}
        </div>

        {loading ? (
          <p style={{ padding: "32px", color: "#aaa", fontFamily: "Georgia, serif", fontSize: "13px" }}>Loading archives...</p>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "52px 32px", textAlign: "center" }}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>🗂️</div>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "14px", color: "#aaa" }}>
              {search ? "No archived users match your search." : "No archived users yet."}
            </p>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#bbb", marginTop: "6px" }}>
              Users deactivated from Manage Users will appear here.
            </p>
          </div>
        ) : (
          filtered.map(user => (
            <div key={user.id} style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr 120px 110px 110px 130px", gap: "8px", padding: "14px 20px", borderBottom: "1px solid #f7f2eb", alignItems: "center", background: "#fdfcfb" }}>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", fontWeight: "700", color: "#aaa" }}>#{user.id}</span>

              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: avatarColor(user.username), display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontFamily: "Georgia, serif", fontSize: "10px", fontWeight: "700", flexShrink: 0 }}>
                  {initials(user.username)}
                </div>
                <div>
                  <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "600", color: "#888" }}>{user.username}</span>
                  <span style={{ display: "block", fontFamily: "Georgia, serif", fontSize: "9px", color: "#c0392b", fontWeight: "700", letterSpacing: "0.06em" }}>DEACTIVATED</span>
                </div>
              </div>

              <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa" }}>{user.contact_number || "—"}</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: "#888" }}>{user.order_count ?? 0} orders</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa" }}>
                {new Date(user.date_joined).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              </span>

              <div style={{ display: "flex", gap: "6px" }}>
                <button onClick={() => setSelected(user)}
                  style={{ background: "#f0ebe3", border: "none", borderRadius: "6px", padding: "5px 8px", cursor: "pointer", fontSize: "12px" }}>
                  👁️
                </button>
                <button onClick={() => handleRestore(user)} disabled={restoring === user.id}
                  style={{ background: "#eefaf4", border: "none", borderRadius: "6px", padding: "5px 10px", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "11px", fontWeight: "700", color: "#3a8a68" }}>
                  {restoring === user.id ? "..." : "✅ Restore"}
                </button>
              </div>
            </div>
          ))
        )}

        {!loading && filtered.length > 0 && (
          <div style={{ padding: "12px 20px", background: "#faf7f2" }}>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa" }}>
              Showing {filtered.length} of {users.length} archived accounts
            </span>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}
          onClick={e => { if (e.target === e.currentTarget) setSelected(null); }}>
          <div style={{ background: "#fff", borderRadius: "18px", padding: "28px", maxWidth: "480px", width: "100%", boxShadow: "0 16px 60px rgba(0,0,0,.15)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ fontFamily: "Georgia, serif", fontSize: "16px", fontWeight: "700", color: "#1a1a1a" }}>Archived User</h3>
              <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px", color: "#aaa" }}>✕</button>
            </div>

            {/* Avatar + name */}
            <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "20px" }}>
              <div style={{ width: "52px", height: "52px", borderRadius: "50%", background: avatarColor(selected.username), display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontFamily: "Georgia, serif", fontSize: "18px", fontWeight: "700" }}>
                {initials(selected.username)}
              </div>
              <div>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "15px", fontWeight: "700", color: "#888" }}>{selected.username}</p>
                <span style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#c0392b", fontWeight: "700", letterSpacing: "0.08em", background: "#fff0f0", padding: "2px 8px", borderRadius: "999px" }}>DEACTIVATED</span>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}>
              {[
                { label: "Email",    value: selected.email },
                { label: "Contact",  value: selected.contact_number || "—" },
                { label: "Role",     value: selected.role },
                { label: "Orders",   value: `${selected.order_count ?? 0} orders` },
                { label: "Joined",   value: new Date(selected.date_joined).toLocaleDateString("en-IN") },
                { label: "Status",   value: "🚫 Deactivated" },
              ].map(({ label, value }) => (
                <div key={label} style={{ background: "#faf7f2", borderRadius: "10px", padding: "12px" }}>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "9px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "4px" }}>{label}</p>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "600", color: "#888" }}>{value}</p>
                </div>
              ))}
            </div>

            {selected.address && (
              <div style={{ background: "#faf7f2", borderRadius: "10px", padding: "12px", marginBottom: "16px" }}>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "9px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "4px" }}>Address</p>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#888" }}>{selected.address}</p>
              </div>
            )}

            <button onClick={() => handleRestore(selected)} disabled={restoring === selected.id}
              style={{ width: "100%", padding: "12px", borderRadius: "10px", border: "none", background: "linear-gradient(135deg, #3a8a68, #2d6e52)", color: "#fff", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700" }}>
              {restoring === selected.id ? "Restoring..." : "✅ Restore This Account"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}